import type { DiarizedSubSegment, SubSegment, WhisperJson } from "@teksta/shared-types";
import type { Cue, CueWord } from "./cues";
import { stringify, stringifyVtt } from "./stringifier";

export type WhisperSubtitleOptions = {
  segments: (SubSegment | DiarizedSubSegment)[];
  format: "srt" | "vtt";
  lines: boolean;
  styleString?: string;
  speakers?: boolean;
};

/** True only when every segment has real word data; mixed coverage uses segment export. */
export const hasWordTimings = <T extends SubSegment>(
  segments: T[]
): segments is (T & { words: NonNullable<SubSegment["words"]> })[] =>
  segments.length > 0 &&
  segments.every((segment) => segment.words?.some((word) => word.word.trim() !== ""));

function wordsToCueWords(
  words: WhisperJson["segments"][number]["words"] | null | undefined
): CueWord[] | undefined {
  if (!words || words.length === 0) return undefined;
  // ElevenLabs Scribe interleaves standalone spacing tokens (`{ word: " " }`)
  // between real words. Keep only real words — the caption renderer adds its own
  // inter-word spacing, so passing space tokens through doubles the gaps and lets
  // the active-word highlight land on blank space. Trim defensively.
  const cueWords = words
    .filter((word) => word.word.trim() !== "")
    .map((word) => ({ start: word.start, end: word.end, text: word.word.trim() }));
  return cueWords.length > 0 ? cueWords : undefined;
}

/** Convert Teksta Whisper JSON segments into source-agnostic editor cues. */
export function whisperJsonToCues(whisperJson: WhisperJson): Cue[] {
  return whisperJson.segments.map((segment) => {
    const words = wordsToCueWords(segment.words);
    return {
      start: segment.start,
      end: segment.end,
      text: segment.text,
      ...(words ? { words } : {}),
    };
  });
}

/** Convert Teksta Whisper segments into SRT or WebVTT subtitle text. */
export const getSrtFromWhisper = ({
  segments,
  format,
  lines,
  styleString,
  speakers = false,
}: WhisperSubtitleOptions): string => {
  const segmentsMillis: (SubSegment | DiarizedSubSegment)[] = [];

  segments.forEach((segment) => {
    if (speakers) {
      segmentsMillis.push({
        ...segment,
        text: `[${segment.speaker}] ${segment.text}`,
        start: Math.round(segment.start * 1000),
        end: Math.round(segment.end * 1000),
      });
    } else {
      segmentsMillis.push({
        ...segment,
        start: Math.round(segment.start * 1000),
        end: Math.round(segment.end * 1000),
      });
    }
  });

  const renderSegmentLevel = () => {
    const clampedSegments = segmentsMillis.map((segment) => {
      if (segment.start >= segment.end) {
        return { ...segment, end: segment.start + 1 };
      }
      return segment;
    });

    if (format === "srt") {
      return stringify(clampedSegments);
    }

    if (format === "vtt") {
      let vttString = stringifyVtt(clampedSegments);
      if (styleString) {
        vttString = vttString.replace(
          /(\d{2}:\d{2}:\d{2}\.\d{3} --> \d{2}:\d{2}:\d{2}\.\d{3})/g,
          `$1 ${styleString}`
        );
      }
      return vttString;
    }
    return "";
  };

  // Word-highlight output requires word timings. Runs produced without them
  // still download segment-level subtitles instead of an empty 200 response.
  if (lines || !hasWordTimings(segmentsMillis)) {
    return renderSegmentLevel();
  } else {
    const stylePrefix = `<font color="#0044ff">`;
    const styleSuffix = `</font>`;

    const resultSegments: SubSegment[] = [];
    for (const segment of segmentsMillis) {
      const segmentsWordsMillis = segment.words.map((word) => ({
        ...word,
        start: Math.round(word.start * 1000),
        end: Math.round(word.end * 1000),
      }));
      for (const [wordIndex, word] of segmentsWordsMillis.entries()) {
        const nextWord = segmentsWordsMillis[wordIndex + 1];
        let text = segmentsWordsMillis
          .map((segmentWord, highlightedWordIndex) => {
            if (highlightedWordIndex === wordIndex) {
              return `${stylePrefix}${segmentWord.word}${styleSuffix}`;
            }
            return `${segmentWord.word}`;
          })
          .join(" ");
        if (speakers) {
          text = `[${segment.speaker}] ${text}`;
        }
        resultSegments.push({
          words: [],
          start: word.start,
          end: Math.max(nextWord ? nextWord.start : word.end, word.start + 1),
          text,
        });
      }
    }

    if (format === "srt") {
      return stringify(resultSegments);
    }

    if (format === "vtt") {
      return stringifyVtt(resultSegments);
    }
  }

  return "";
};
